import { Preview3DConfigurationManager, SceneEditor } from '@zakeke/zakeke-3d-core';
import { PropertiesPanelPropertyConfig } from '../../../../widgets/propertiesPanel';
import { useDialogManager } from '../../../../widgets/dialog';
import React, { useContext } from 'react';
import { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import { T, color3ToHex, hexToRgb01 } from 'src/shared/helpers';
import ReplaceSolid from 'src/shared/material_icons/action/find_replace.svg';
import DAMDialog from '../../dialogs/damDialog';
import { UploadDialog } from '../../dialogs/uploadDialog';

export type MaterialConfigHelpers = {
    sceneManager: Preview3DConfigurationManager;
    sceneEditor: SceneEditor;
    updateState: <T extends BABYLON.Material>(material: T, refreshMaterials?: boolean) => void;
    uploadTexture: <T extends BABYLON.Material>(material: T, type: string, mobile: boolean, files: FileList) => Promise<void>;
    removeTexture: <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => void;
    getTexturePreviewUrl: <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => string | null;
    isTextureLoading: <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => boolean;
    handleMaterialNameChange?: (newValue: string) => void;
    designMaterialOverrideColor?: {
        checked: boolean;
        onChange: (checked: boolean) => void;
    };
};

const createPropertyConfig = (config: PropertiesPanelPropertyConfig) => config;

// LE ESTENSIONI CHE UNO SLOT DI TEXTURE ACCETTA, e sono quelle del vecchio backoffice: una mappa
// di ambiente o una cube map arrivano in `.hdr` o `.env`, che immagini non sono e che un filtro
// sulle sole immagini terrebbe fuori dal selettore. Senza questa riga valeva il valore di
// riserva del controllo, che filtrava su un tipo MIME inesistente: il selettore si apriva vuoto.
const TEXTURE_FILE_TYPES = 'image/*,.hdr,.env';

const createTexturePropertyConfig = (material: BABYLON.Material, helpers: MaterialConfigHelpers, type: string) => {
    const isLoading = helpers.isTextureLoading(material, type, false);
    const url = helpers.getTexturePreviewUrl(material, type, false);
    const mobileUrl = helpers.getTexturePreviewUrl(material, type, true);

    return [
        createPropertyConfig({
            type: 'image',
            id: type + '-image',
            label: T._('Texture', 'Admin'),
            disabled: isLoading,
            accept: TEXTURE_FILE_TYPES,
            images: url ? [{ fileName: '', src: url }] : null,
            onUpload: (files) => helpers.uploadTexture(material, type, false, files!),
            onDelete: () => helpers.removeTexture(material, type, false)
        }),
        (material as any)[type + 'Texture'] &&
            mobileUrl !== '' &&
            createPropertyConfig({
                type: 'image',
                id: type + '-image',
                label: T._('Mobile version', 'Admin'),
                disabled: isLoading,
                accept: TEXTURE_FILE_TYPES,
                images: mobileUrl ? [{ fileName: '', src: mobileUrl }] : null,
                onUpload: (files) => helpers.uploadTexture(material, type, true, files!),
                onDelete: () => helpers.removeTexture(material, type, true)
            }),
        (material as any)[type + 'Texture']
            ? createPropertyConfig({
                  type: 'point',
                  id: type + '-texture-scaling',
                  label: T._('Scaling', 'Admin'),
                  coords: [
                      {
                          id: 'u',
                          label: 'U',
                          value: ((material as any)[type + 'Texture'] as BABYLON.Texture).uScale,
                          onChange: (newValue) => {
                              ((material as any)[type + 'Texture'] as BABYLON.Texture).uScale = newValue;
                              helpers.updateState(material);
                          }
                      },
                      {
                          id: 'V',
                          label: 'V',
                          value: ((material as any)[type + 'Texture'] as BABYLON.Texture).vScale,
                          onChange: (newValue) => {
                              ((material as any)[type + 'Texture'] as BABYLON.Texture).vScale = newValue;
                              helpers.updateState(material);
                          }
                      }
                  ]
              })
            : null,

        (material as any)[type + 'Texture']
            ? createPropertyConfig({
                  type: 'point',
                  id: type + '-uv-channel',
                  label: T._('UV Channel', 'Admin'),
                  coords: [
                      {
                          id: '',
                          label: '',
                          value: ((material as any)[type + 'Texture'] as BABYLON.Texture).coordinatesIndex,
                          onChange: (newValue) => {
                              ((material as any)[type + 'Texture'] as BABYLON.Texture).coordinatesIndex = newValue;
                              helpers.updateState(material);
                          }
                      }
                  ]
              })
            : null,

        (material as any)[type + 'Texture']
            ? createPropertyConfig({
                  type: 'checkbox',
                  id: type + '-has-alpha',
                  label: T._('Has Alpha', 'Admin'),
                  checked: (material as any)[type + 'Texture'].hasAlpha,
                  onChange: (checked) => {
                      (material as any)[type + 'Texture'].hasAlpha = checked;
                      helpers.updateState(material);
                  }
              })
            : null
    ].filter(Boolean) as PropertiesPanelPropertyConfig[];
};

const standardMaterialProperties = (material: BABYLON.StandardMaterial, helpers: MaterialConfigHelpers): PropertiesPanelPropertyConfig[] => {
    return [
        createPropertyConfig({
            type: 'group',
            id: 'diffuse',
            header: T._('Diffuse', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'diffuseColor',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.diffuseColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.diffuseColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture code
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'ambient',
            header: T._('Ambient', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'ambientColor',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.ambientColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.ambientColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture code
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'specular',
            header: T._('Specular', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'specularColor',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.specularColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.specularColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture code
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'emissive',
            header: T._('Emissive', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'emissiveColor',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.emissiveColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.emissiveColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture code
                // 	fresnel: true
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'reflection',
            header: T._('Reflection', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: true
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'bump',
            header: T._('Bump', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: false
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'opacity',
            header: T._('Opacity', 'Admin'),
            defaultIsOpen: false,
            properties: [
                ...createTexturePropertyConfig(material, helpers, 'opacity')
                // 	fresnel: true
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'lightmap',
            header: T._('Lightmap', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: false
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        })
    ];
};

const pbrMetallicRoughnessMaterialProperties = (
    material: BABYLON.PBRMetallicRoughnessMaterial,
    helpers: MaterialConfigHelpers
): PropertiesPanelPropertyConfig[] => {
    return [
        createPropertyConfig({
            type: 'group',
            id: 'base',
            header: T._('Base', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'baseColor',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.baseColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.baseColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture code
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'emissive',
            header: T._('Emissive', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'emissiveColor',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.emissiveColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.emissiveColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture code
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'metallic-roughness',
            header: T._('Metallic Roughness', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'range',
                    id: 'metallic',
                    label: T._('Metallic', 'Admin'),
                    value: material.metallic,
                    min: 0,
                    max: 1,
                    step: 0.01
                    // onChange: (newValue) => onChange('metallic', newValue.toString())
                }),
                createPropertyConfig({
                    type: 'range',
                    id: 'roughness',
                    label: T._('Roughness', 'Admin'),
                    value: material.roughness,
                    min: 0,
                    max: 1,
                    step: 0.01
                    // onChange: (newValue) => onChange('roughness', newValue.toString())
                })
                // TODO add texture
                // 	new MaterialProperty(MaterialPropertyType.Texture, { name: 'metallicRoughness' })
            ]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'environment',
            header: T._('Environment', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: false
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'normal',
            header: T._('Normal', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: false
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'occlusion',
            header: T._('Occlusion', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: false
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        })
    ];
};

const pbrSpecularGlossinessMaterialProperties = (material: BABYLON.PBRSpecularGlossinessMaterial, helpers: MaterialConfigHelpers) => {
    return [
        createPropertyConfig({
            type: 'group',
            id: 'diffuse',
            header: T._('Diffuse', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'diffuseColor',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.diffuseColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.diffuseColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture code
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'emissive',
            header: T._('Emissive', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'emissiveColor',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.emissiveColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.emissiveColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture code
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'specular-glossiness',
            header: T._('Specular Glossiness', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'range',
                    id: 'glossiness',
                    label: T._('Glossiness', 'Admin'),
                    value: material.glossiness,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.glossiness = newValue;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'color',
                    id: 'specular',
                    label: T._('Specular', 'Admin'),
                    value: color3ToHex(material.specularColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.specularColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                })
                // TODO add texture
                // 	new MaterialProperty(MaterialPropertyType.Texture, { name: 'specularGlossiness' })
            ]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'environment',
            header: T._('Environment', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: false
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'normal',
            header: T._('Normal', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: false
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        }),
        createPropertyConfig({
            type: 'group',
            id: 'occlusion',
            header: T._('Occlusion', 'Admin'),
            defaultIsOpen: false,
            properties: [
                // TODO add texture code
                // 	fresnel: false
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        })
    ];
};

const pbrMaterialProperties = (material: BABYLON.PBRMaterial, helpers: MaterialConfigHelpers): PropertiesPanelPropertyConfig[] => {
    const createIntensityGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'intensity',
            header: T._('Intensity', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'range',
                    id: 'direct-intensity',
                    label: T._('Direct', 'Admin'),
                    value: material.directIntensity,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.directIntensity = newValue;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'range',
                    id: 'emissive-intensity',
                    label: T._('Emissive', 'Admin'),
                    value: material.emissiveIntensity,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.emissiveIntensity = newValue;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'range',
                    id: 'environment-intensity',
                    label: T._('Environment', 'Admin'),
                    value: material.environmentIntensity,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.environmentIntensity = newValue;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'range',
                    id: 'specular-intensity',
                    label: T._('Specular', 'Admin'),
                    value: material.specularIntensity,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.specularIntensity = newValue;
                        helpers.updateState(material);
                    }
                })
            ]
        });

    const createCameraGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'camera',
            header: T._('Camera', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'range',
                    id: 'camera-exposure',
                    label: T._('Exposure', 'Admin'),
                    value: material.cameraExposure,
                    min: 0,
                    max: 1,
                    onChange: (newValue) => {
                        material.cameraExposure = newValue;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'range',
                    id: 'camera-contrast',
                    label: T._('Contrast', 'Admin'),
                    value: material.cameraContrast,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.cameraContrast = newValue;
                        helpers.updateState(material);
                    }
                }),
                ...createTexturePropertyConfig(material, helpers, 'cameraColorGrading')
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createAlbedoGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'albedo',
            header: T._('Albedo', 'Admin'),
            defaultIsOpen: false,
            properties: [
                helpers.designMaterialOverrideColor
                    ? createPropertyConfig({
                          type: 'checkbox',
                          id: 'override-albedo-color',
                          label: T._('Override color', 'Admin'),
                          checked: helpers.designMaterialOverrideColor.checked,
                          onChange: helpers.designMaterialOverrideColor.onChange
                      })
                    : null,
                (!helpers.designMaterialOverrideColor || helpers.designMaterialOverrideColor.checked) && [
                    createPropertyConfig({
                        type: 'color',
                        id: 'albedo-color',
                        label: T._('Color', 'Admin'),
                        // material.albedoColor.toHexString() is not working
                        value: color3ToHex(material.albedoColor),
                        onChange: (newValue) => {
                            const rgbValue = hexToRgb01(newValue);
                            material.albedoColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                            helpers.updateState(material);
                        }
                    }),
                    ...createTexturePropertyConfig(material, helpers, 'albedo'),
                    material.albedoTexture
                        ? createPropertyConfig({
                              type: 'checkbox',
                              id: 'use-alpha-from-albedo-texture',
                              label: T._('Use alpha from albedo texture', 'Admin'),
                              checked: material.useAlphaFromAlbedoTexture,
                              onChange: (checked) => {
                                  material.useAlphaFromAlbedoTexture = checked;
                                  helpers.updateState(material);
                              }
                          })
                        : null
                ]
            ]
                .flat()
                .filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createAmbientGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'ambient',
            header: T._('Ambient', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'ambient-color',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.ambientColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.ambientColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                }),
                ...createTexturePropertyConfig(material, helpers, 'ambient'),
                material.ambientTexture
                    ? createPropertyConfig({
                          type: 'checkbox',
                          id: 'use-ambient-in-gray-scale',
                          label: T._('Use ambient in gray scale', 'Admin'),
                          checked: material.useAmbientInGrayScale,
                          onChange: (checked) => {
                              material.useAmbientInGrayScale = checked;
                              helpers.updateState(material);
                          }
                      })
                    : null
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });
    const createOpacityGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'opacity',
            header: T._('Opacity', 'Admin'),
            defaultIsOpen: false,
            properties: [
                ...createTexturePropertyConfig(material, helpers, 'opacity')
                // 	fresnel: true
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createRefractionGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'refraction-texture',
            header: T._('Refraction', 'Admin'),
            defaultIsOpen: false,
            properties: [...createTexturePropertyConfig(material, helpers, 'refraction')].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createAnisotropicConfig = () =>
        createPropertyConfig({
            type: 'group',
            id: 'clear-coat-anisotropic',
            header: T._('Anisotropic', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'anisotropic-isEnabled',
                    label: T._('Enabled', 'Admin'),
                    checked: material.anisotropy.isEnabled,
                    onChange: (checked) => {
                        material.anisotropy.isEnabled = checked;
                        helpers.updateState(material);
                    }
                }),
                material.anisotropy.isEnabled && [
                    createPropertyConfig({
                        type: 'range',
                        id: 'anisotropic-intensity',
                        label: T._('Intensity', 'Admin'),
                        min: 0,
                        max: 1,
                        step: 0.01,
                        value: material.anisotropy.intensity,
                        onChange: (newValue) => {
                            material.anisotropy.intensity = newValue;
                            helpers.updateState(material);
                        }
                    }),
                    createPropertyConfig({
                        type: 'point',
                        id: 'anisotropic-direction',
                        label: T._('Direction', 'Admin'),
                        coords: [
                            {
                                id: 'x',
                                label: 'X',
                                value: material.anisotropy.direction.x,
                                onChange: (newValue) => {
                                    material.anisotropy.direction.set(newValue, material.anisotropy.direction.y);
                                    helpers.updateState(material);
                                }
                            },
                            {
                                id: 'y',
                                label: 'Y',
                                value: material.anisotropy.direction.y,
                                onChange: (newValue) => {
                                    material.anisotropy.direction.set(material.anisotropy.direction.x, newValue);
                                    helpers.updateState(material);
                                }
                            }
                        ]
                    }),
                    ...createTexturePropertyConfig(material, helpers, 'anisotropy_texture')
                ]
            ]
                .flat()
                .filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createSheenConfig = () =>
        createPropertyConfig({
            type: 'group',
            id: 'sheen',
            header: T._('Sheen', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'sheen-isEnabled',
                    label: T._('Enabled', 'Admin'),
                    checked: material.sheen.isEnabled,
                    onChange: (checked) => {
                        material.sheen.isEnabled = checked;
                        helpers.updateState(material);
                    }
                }),
                material.sheen.isEnabled && [
                    createPropertyConfig({
                        type: 'range',
                        id: 'sheen-intensity',
                        label: T._('Intensity', 'Admin'),
                        min: 0,
                        max: 1,
                        step: 0.01,
                        value: material.sheen.intensity,
                        onChange: (newValue) => {
                            material.sheen.intensity = newValue;
                            helpers.updateState(material);
                        }
                    }),
                    createPropertyConfig({
                        type: 'checkbox',
                        id: 'sheen-albedoScaling',
                        label: T._('Albedo Scaling', 'Admin'),
                        checked: material.sheen.albedoScaling,
                        onChange: (checked) => {
                            material.sheen.albedoScaling = checked;
                            helpers.updateState(material);
                        }
                    }),
                    createPropertyConfig({
                        type: 'checkbox',
                        id: 'sheen-linkSheenWithAlbedo',
                        label: T._('Link Sheen With Albedo', 'Admin'),
                        checked: material.sheen.linkSheenWithAlbedo ?? false,
                        onChange: (checked) => {
                            material.sheen.linkSheenWithAlbedo = checked;
                            helpers.updateState(material);
                        }
                    }),
                    (material.sheen.linkSheenWithAlbedo ?? false) &&
                        createPropertyConfig({
                            type: 'range',
                            id: 'sheen-roughness',
                            label: T._('Roughness', 'Admin'),
                            min: 0,
                            max: 1,
                            step: 0.01,
                            value: material.sheen.roughness ?? 0,
                            onChange: (newValue) => {
                                material.sheen.roughness = newValue;
                                helpers.updateState(material);
                            }
                        }),
                    createPropertyConfig({
                        type: 'color',
                        id: 'sheen-color',
                        label: T._('Color', 'Admin'),
                        value: color3ToHex(material.sheen.color),
                        onChange: (newValue) => {
                            const rgbValue = hexToRgb01(newValue);
                            material.sheen.color.set(rgbValue.r, rgbValue.g, rgbValue.b);
                            helpers.updateState(material);
                        }
                    }),
                    ...createTexturePropertyConfig(material, helpers, 'sheen_texture'),
                    (material.sheen.linkSheenWithAlbedo ?? false) &&
                        createPropertyConfig({
                            type: 'group',
                            id: 'sheen-roughness-texture',
                            header: T._('Roughness Texture', 'Admin'),
                            defaultIsOpen: false,
                            properties: [
                                createPropertyConfig({
                                    type: 'checkbox',
                                    id: 'sheen-roughness-useRoughnessFromMainTexture',
                                    label: T._('Use roughness from main texture', 'Admin'),
                                    checked: material.sheen.useRoughnessFromMainTexture ?? false,
                                    onChange: (checked) => {
                                        material.sheen.useRoughnessFromMainTexture = checked;
                                        helpers.updateState(material);
                                    }
                                }),
                                !material.sheen.useRoughnessFromMainTexture && [...createTexturePropertyConfig(material, helpers, 'sheen_textureRoughness')]
                            ]
                                .flat()
                                .filter(Boolean) as PropertiesPanelPropertyConfig[]
                        })
                ]
            ]
                .flat()
                .filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createSubSurfaceConfig = () =>
        createPropertyConfig({
            type: 'group',
            id: 'sub-surface',
            header: T._('SubSurface', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'sub-surface-useGltfStyleTextures',
                    label: T._('Use gltf style textures', 'Admin'),
                    checked: material.subSurface.useGltfStyleTextures,
                    onChange: (checked) => {
                        material.subSurface.useGltfStyleTextures = checked;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'group',
                    id: 'sub-surface-thickness',
                    header: T._('Thickness', 'Admin'),
                    defaultIsOpen: false,
                    properties: [
                        createPropertyConfig({
                            type: 'range',
                            id: 'sub-surface-thickness-maximumThickness',
                            label: T._('Maximum thickness', 'Admin'),
                            min: 0,
                            max: 1,
                            step: 0.01,
                            value: material.subSurface.maximumThickness,
                            onChange: (newValue) => {
                                material.subSurface.maximumThickness = newValue;
                                helpers.updateState(material);
                            }
                        }),
                        createPropertyConfig({
                            type: 'range',
                            id: 'sub-surface-thickness-minimumThickness',
                            label: T._('Minimum thickness', 'Admin'),
                            min: 0,
                            max: 1,
                            step: 0.01,
                            value: material.subSurface.minimumThickness,
                            onChange: (newValue) => {
                                material.subSurface.minimumThickness = newValue;
                                helpers.updateState(material);
                            }
                        }),
                        createPropertyConfig({
                            type: 'checkbox',
                            id: 'sub-surface-thickness-useMaskFromThicknessTexture',
                            label: T._('Use mask from thickness texture', 'Admin'),
                            checked: material.subSurface.useMaskFromThicknessTexture,
                            onChange: (checked) => {
                                material.subSurface.useMaskFromThicknessTexture = checked;
                                helpers.updateState(material);
                            }
                        }),
                        createPropertyConfig({
                            type: 'checkbox',
                            id: 'sub-surface-thickness-useThicknessAsDepth',
                            label: T._('Use thickness as depth', 'Admin'),
                            checked: material.subSurface.useThicknessAsDepth,
                            onChange: (checked) => {
                                material.subSurface.useThicknessAsDepth = checked;
                                helpers.updateState(material);
                            }
                        }),
                        ...createTexturePropertyConfig(material, helpers, 'subSurface_thickness')
                    ]
                }),
                createPropertyConfig({
                    type: 'group',
                    id: 'sub-surface-tint',
                    header: T._('Tint', 'Admin'),
                    defaultIsOpen: false,
                    properties: [
                        createPropertyConfig({
                            type: 'color',
                            id: 'sub-surface-tint-tintColor',
                            label: T._('Tint color', 'Admin'),
                            value: color3ToHex(material.subSurface.tintColor),
                            onChange: (newValue) => {
                                const rgbValue = hexToRgb01(newValue);
                                material.subSurface.tintColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                                helpers.updateState(material);
                            }
                        }),
                        createPropertyConfig({
                            type: 'range',
                            id: 'sub-surface-tint-tintColorAtDistance',
                            label: T._('Tint color at distance', 'Admin'),
                            min: 0,
                            max: 1,
                            step: 0.01,
                            value: material.subSurface.tintColorAtDistance,
                            onChange: (newValue) => {
                                material.subSurface.tintColorAtDistance = newValue;
                                helpers.updateState(material);
                            }
                        })
                    ]
                }),
                createPropertyConfig({
                    type: 'group',
                    id: 'sub-surface-refraction',
                    header: T._('Refraction', 'Admin'),
                    defaultIsOpen: false,
                    properties: [
                        createPropertyConfig({
                            type: 'checkbox',
                            id: 'sub-surface-refraction-isRefractionEnabled',
                            label: T._('Enabled', 'Admin'),
                            checked: material.subSurface.isRefractionEnabled,
                            onChange: (checked) => {
                                material.subSurface.isRefractionEnabled = checked;
                                helpers.updateState(material);
                            }
                        }),
                        material.subSurface.isRefractionEnabled && [
                            createPropertyConfig({
                                type: 'range',
                                id: 'sub-surface-refraction-refractionIntensity',
                                label: T._('Intensity', 'Admin'),
                                min: 0,
                                max: 1,
                                step: 0.01,
                                value: material.subSurface.refractionIntensity,
                                onChange: (newValue) => {
                                    material.subSurface.refractionIntensity = newValue;
                                    helpers.updateState(material);
                                }
                            }),
                            createPropertyConfig({
                                type: 'range',
                                id: 'sub-surface-refraction-indexOfRefraction',
                                label: T._('Index of refraction', 'Admin'),
                                min: 0,
                                max: 1,
                                step: 0.01,
                                value: material.subSurface.indexOfRefraction,
                                onChange: (newValue) => {
                                    material.subSurface.indexOfRefraction = newValue;
                                    helpers.updateState(material);
                                }
                            }),
                            createPropertyConfig({
                                type: 'checkbox',
                                id: 'sub-surface-refraction-invertRefractionY',
                                label: T._('Invert refraction Y', 'Admin'),
                                checked: material.subSurface.invertRefractionY,
                                onChange: (checked) => {
                                    material.subSurface.invertRefractionY = checked;
                                    helpers.updateState(material);
                                }
                            }),
                            createPropertyConfig({
                                type: 'checkbox',
                                id: 'sub-surface-refraction-linkRefractionWithTransparency',
                                label: T._('Link refraction with transparency', 'Admin'),
                                checked: material.subSurface.linkRefractionWithTransparency,
                                onChange: (checked) => {
                                    material.subSurface.linkRefractionWithTransparency = checked;
                                    helpers.updateState(material);
                                }
                            }),
                            createPropertyConfig({
                                type: 'checkbox',
                                id: 'sub-surface-refraction-useAlbedoToTintRefraction',
                                label: T._('Use albedo to tint refraction', 'Admin'),
                                checked: material.subSurface.useAlbedoToTintRefraction,
                                onChange: (checked) => {
                                    material.subSurface.useAlbedoToTintRefraction = checked;
                                    helpers.updateState(material);
                                }
                            }),
                            ...createTexturePropertyConfig(material, helpers, 'subSurface_refractionIntensity')
                        ]
                    ]
                        .flat()
                        .filter(Boolean) as PropertiesPanelPropertyConfig[]
                }),
                createPropertyConfig({
                    type: 'group',
                    id: 'sub-surface-translucency',
                    header: T._('Translucency', 'Admin'),
                    defaultIsOpen: false,
                    properties: [
                        createPropertyConfig({
                            type: 'checkbox',
                            id: 'sub-surface-translucency-isTranslucencyEnabled',
                            label: T._('Enabled', 'Admin'),
                            checked: material.subSurface.isTranslucencyEnabled,
                            onChange: (checked) => {
                                material.subSurface.isTranslucencyEnabled = checked;
                                helpers.updateState(material);
                            }
                        }),
                        material.subSurface.isTranslucencyEnabled && [
                            createPropertyConfig({
                                type: 'checkbox',
                                id: 'sub-surface-translucency-useAlbedoToTintTranslucency',
                                label: T._('Use albedo to tint translucency', 'Admin'),
                                checked: material.subSurface.useAlbedoToTintTranslucency,
                                onChange: (checked) => {
                                    material.subSurface.useAlbedoToTintTranslucency = checked;
                                    helpers.updateState(material);
                                }
                            }),
                            createPropertyConfig({
                                type: 'color',
                                id: 'sub-surface-translucency-diffusionDistance',
                                label: T._('Diffusion distance', 'Admin'),
                                value: color3ToHex(material.subSurface.diffusionDistance),
                                onChange: (newValue) => {
                                    const rgbValue = hexToRgb01(newValue);
                                    material.subSurface.diffusionDistance.set(rgbValue.r, rgbValue.g, rgbValue.b);
                                    helpers.updateState(material);
                                }
                            }),
                            createPropertyConfig({
                                type: 'range',
                                id: 'sub-surface-translucency-translucencyIntensity',
                                label: T._('Translucency intensity', 'Admin'),
                                min: 0,
                                max: 1,
                                step: 0.01,
                                value: material.subSurface.translucencyIntensity,
                                onChange: (newValue) => {
                                    material.subSurface.translucencyIntensity = newValue;
                                    helpers.updateState(material);
                                }
                            }),
                            ...createTexturePropertyConfig(material, helpers, 'subSurface_translucencyIntensity')
                        ]
                    ]
                        .flat()
                        .filter(Boolean) as PropertiesPanelPropertyConfig[]
                })
            ]
        });

    const createReflectionGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'reflection',
            header: T._('Reflection', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'reflection-color',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.reflectionColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.reflectionColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                }),
                ...createTexturePropertyConfig(material, helpers, 'reflection')
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createEmissiveGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'emissive',
            header: T._('Emissive', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'emissive-color',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.emissiveColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.emissiveColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                }),
                ...createTexturePropertyConfig(material, helpers, 'emissive')
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createReflectivityGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'refectivity',
            header: T._('Refectivity', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'reflectivity-color',
                    label: T._('Color', 'Admin'),
                    value: color3ToHex(material.reflectivityColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.reflectivityColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                }),
                ...createTexturePropertyConfig(material, helpers, 'reflectivity')
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createMetallicGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'metallic',
            header: T._('Metallic', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'range',
                    id: 'metallic',
                    label: T._('Metallic', 'Admin'),
                    value: material.metallic || 0,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.metallic = newValue;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'range',
                    id: 'roughness',
                    label: T._('Roughness', 'Admin'),
                    value: material.roughness || 0,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.roughness = newValue;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'color',
                    id: 'metallic-reflectance-color',
                    label: T._('Reflectance color', 'Admin'),
                    value: color3ToHex(material.metallicReflectanceColor),
                    onChange: (newValue) => {
                        const rgbValue = hexToRgb01(newValue);
                        material.metallicReflectanceColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                        helpers.updateState(material);
                    }
                }),
                ...createTexturePropertyConfig(material, helpers, 'metallic'),
                material.metallicTexture
                    ? createPropertyConfig({
                          type: 'checkbox',
                          id: 'use-roughness-from-alpha',
                          label: T._('Use roughness from alpha', 'Admin'),
                          checked: material.useRoughnessFromMetallicTextureAlpha,
                          onChange: (checked) => {
                              material.useRoughnessFromMetallicTextureAlpha = checked;
                              helpers.updateState(material);
                          }
                      })
                    : null,
                material.metallicTexture
                    ? createPropertyConfig({
                          type: 'checkbox',
                          id: 'use-roughness-from-green',
                          label: T._('Use roughness from green', 'Admin'),
                          checked: material.useRoughnessFromMetallicTextureGreen,
                          onChange: (checked) => {
                              material.useRoughnessFromMetallicTextureGreen = checked;
                              helpers.updateState(material);
                          }
                      })
                    : null,
                material.metallicTexture
                    ? createPropertyConfig({
                          type: 'checkbox',
                          id: 'use-metallness-from-blue',
                          label: T._('Use metallness from blue', 'Admin'),
                          checked: material.useMetallnessFromMetallicTextureBlue,
                          onChange: (checked) => {
                              material.useMetallnessFromMetallicTextureBlue = checked;
                              helpers.updateState(material);
                          }
                      })
                    : null,
                material.metallicTexture
                    ? createPropertyConfig({
                          type: 'checkbox',
                          id: 'use-ambient-occlusion-from-red',
                          label: T._('Use ambient occlusion from red', 'Admin'),
                          checked: material.useAmbientOcclusionFromMetallicTextureRed,
                          onChange: (checked) => {
                              material.useAmbientOcclusionFromMetallicTextureRed = checked;
                              helpers.updateState(material);
                          }
                      })
                    : null,
                material.metallicTexture
                    ? createPropertyConfig({
                          type: 'range',
                          id: 'f0-factor',
                          label: T._('F0 factor', 'Admin'),
                          value: material.metallicF0Factor,
                          min: 0,
                          max: 1,
                          step: 0.01,
                          onChange: (newValue) => {
                              material.metallicF0Factor = newValue;
                              helpers.updateState(material);
                          }
                      })
                    : null
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createMicroSurfaceGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'micro-surface',
            header: T._('Micro Surface', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'range',
                    id: 'micro-surface',
                    label: T._('Micro Surface', 'Admin'),
                    value: material.microSurface,
                    min: 0,
                    max: 1,
                    step: 0.01,
                    onChange: (newValue) => {
                        material.microSurface = newValue;
                        helpers.updateState(material);
                    }
                }),
                ...createTexturePropertyConfig(material, helpers, 'microSurface')
            ]
        });

    const createNormalGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'normal',
            header: T._('Normal', 'Admin'),
            defaultIsOpen: false,
            properties: [
                ...createTexturePropertyConfig(material, helpers, 'bump'),
                material.bumpTexture
                    ? createPropertyConfig({
                          type: 'checkbox',
                          id: 'invert-normal-map-X',
                          label: T._('Invert normal map X', 'Admin'),
                          checked: material.invertNormalMapX,
                          onChange: (checked) => {
                              material.invertNormalMapX = checked;
                              helpers.updateState(material);
                          }
                      })
                    : null,
                material.bumpTexture
                    ? createPropertyConfig({
                          type: 'checkbox',
                          id: 'invert-normal-map-Y',
                          label: T._('Invert normal map Y', 'Admin'),
                          checked: material.invertNormalMapY,
                          onChange: (checked) => {
                              material.invertNormalMapY = checked;
                              helpers.updateState(material);
                          }
                      })
                    : null
            ].filter(Boolean) as PropertiesPanelPropertyConfig[]
        });

    const createLightmapGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'lightmap',
            header: T._('Lightmap', 'Admin'),
            defaultIsOpen: false,
            properties: [...createTexturePropertyConfig(material, helpers, 'lightmap')]
        });

    const createFlagsGroup = () =>
        createPropertyConfig({
            type: 'group',
            id: 'flags',
            header: T._('Flags', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'useLightmapAsShadowmap',
                    label: T._('Lightmap as shadowmap', 'Admin'),
                    checked: material.useLightmapAsShadowmap,
                    onChange: (checked) => {
                        material.useLightmapAsShadowmap = checked;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'useSpecularOverAlpha',
                    label: T._('Specular over alpha', 'Admin'),
                    checked: material.useSpecularOverAlpha,
                    onChange: (checked) => {
                        material.useSpecularOverAlpha = checked;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'usePhysicalLightFalloff',
                    label: T._('Physical light falloff', 'Admin'),
                    checked: material.usePhysicalLightFalloff,
                    onChange: (checked) => {
                        material.usePhysicalLightFalloff = checked;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'useRadianceOverAlpha',
                    label: T._('Radiance over alpha', 'Admin'),
                    checked: material.useRadianceOverAlpha,
                    onChange: (checked) => {
                        material.useRadianceOverAlpha = checked;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'useParallax',
                    label: T._('Parallax', 'Admin'),
                    checked: material.useParallax,
                    onChange: (checked) => {
                        material.useParallax = checked;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'useParallaxOcclusion',
                    label: T._('Parallax occlusion', 'Admin'),
                    checked: material.useParallaxOcclusion,
                    onChange: (checked) => {
                        material.useParallaxOcclusion = checked;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'disableLighting',
                    label: T._('Disable lighting', 'Admin'),
                    checked: material.disableLighting,
                    onChange: (checked) => {
                        material.disableLighting = checked;
                        helpers.updateState(material);
                    }
                }),
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'twoSidedLighting',
                    label: T._('Two sided lighting', 'Admin'),
                    checked: material.twoSidedLighting,
                    onChange: (checked) => {
                        material.twoSidedLighting = checked;
                        helpers.updateState(material);
                    }
                })
            ]
        });

    const createClearCoatGroup = () => {
        return createPropertyConfig({
            type: 'group',
            id: 'clear-coat',
            header: T._('Clear Coat', 'Admin'),
            defaultIsOpen: false,
            properties: [
                createPropertyConfig({
                    type: 'checkbox',
                    id: 'clear-coat-is-enabled',
                    label: T._('Enabled', 'Admin'),
                    checked: material.clearCoat.isEnabled,
                    onChange: (checked) => {
                        material.clearCoat.isEnabled = checked;
                        helpers.updateState(material);
                    }
                }),
                material.clearCoat.isEnabled && [
                    createPropertyConfig({
                        type: 'range',
                        id: 'clear-coat-intensity',
                        label: T._('Intensity', 'Admin'),
                        value: material.clearCoat.intensity,
                        min: 0,
                        max: 1,
                        step: 0.01,
                        onChange: (newValue) => {
                            material.clearCoat.intensity = newValue;
                            helpers.updateState(material);
                        }
                    }),
                    createPropertyConfig({
                        type: 'range',
                        id: 'clear-coat-roughness',
                        label: T._('Roughness', 'Admin'),
                        value: material.clearCoat.roughness,
                        min: 0,
                        max: 1,
                        step: 0.01,
                        onChange: (newValue) => {
                            material.clearCoat.roughness = newValue;
                            helpers.updateState(material);
                        }
                    }),
                    createPropertyConfig({
                        type: 'range',
                        id: 'clear-coat-indexOfRefraction',
                        label: T._('Index of Refraction', 'Admin'),
                        min: 0,
                        max: 2,
                        step: 0.01,
                        value: material.clearCoat.indexOfRefraction,
                        onChange: (newValue) => {
                            material.clearCoat.indexOfRefraction = newValue;
                            helpers.updateState(material);
                        }
                    }),
                    createPropertyConfig({
                        type: 'checkbox',
                        id: 'clear-coat-remapF0OnInterfaceChange',
                        label: T._('Remap F0', 'Admin'),
                        checked: material.clearCoat.remapF0OnInterfaceChange,
                        onChange: (checked) => {
                            material.clearCoat.remapF0OnInterfaceChange = checked;
                            helpers.updateState(material);
                        }
                    }),
                    createPropertyConfig({
                        type: 'group',
                        id: 'clear-coat-bump',
                        header: T._('Bump', 'Admin'),
                        defaultIsOpen: false,
                        properties: [...createTexturePropertyConfig(material, helpers, 'clearCoat_bump')]
                    }),
                    createPropertyConfig({
                        type: 'group',
                        id: 'clear-coat-tint',
                        header: T._('Tint', 'Admin'),
                        defaultIsOpen: false,
                        properties: [
                            createPropertyConfig({
                                type: 'checkbox',
                                id: 'clear-coat-tint-isEnabled',
                                label: T._('Enabled', 'Admin'),
                                checked: material.clearCoat.isTintEnabled,
                                onChange: (checked) => {
                                    material.clearCoat.isTintEnabled = checked;
                                    helpers.updateState(material);
                                }
                            }),
                            material.clearCoat.isTintEnabled && [
                                createPropertyConfig({
                                    type: 'color',
                                    id: 'clear-coat-tint-color',
                                    label: T._('Color', 'Admin'),
                                    value: color3ToHex(material.clearCoat.tintColor),
                                    onChange: (newValue) => {
                                        const rgbValue = hexToRgb01(newValue);
                                        material.clearCoat.tintColor.set(rgbValue.r, rgbValue.g, rgbValue.b);
                                        helpers.updateState(material);
                                    }
                                }),
                                createPropertyConfig({
                                    type: 'range',
                                    id: 'clear-coat-tint-tintColorAtDistance',
                                    label: T._('Tint Color At Distance', 'Admin'),
                                    min: 0,
                                    max: 1,
                                    step: 0.01,
                                    value: material.clearCoat.tintColorAtDistance,
                                    onChange: (newValue) => {
                                        material.clearCoat.tintColorAtDistance = newValue;
                                        helpers.updateState(material);
                                    }
                                }),
                                createPropertyConfig({
                                    type: 'range',
                                    id: 'clear-coat-tint-tintThickness',
                                    label: T._('Tint Thickness', 'Admin'),
                                    min: 0,
                                    max: 1,
                                    step: 0.01,
                                    value: material.clearCoat.tintThickness,
                                    onChange: (newValue) => {
                                        material.clearCoat.tintThickness = newValue;
                                        helpers.updateState(material);
                                    }
                                }),
                                ...createTexturePropertyConfig(material, helpers, 'clearCoat_tint')
                            ]
                        ]
                            .flat()
                            .filter(Boolean) as PropertiesPanelPropertyConfig[]
                    }),
                    createPropertyConfig({
                        type: 'group',
                        id: 'clear-coat-roughness',
                        header: T._('Roughness', 'Admin'),
                        defaultIsOpen: false,
                        properties: [
                            createPropertyConfig({
                                type: 'checkbox',
                                id: 'clear-coat-roughness-useRoughnessFromMainTexture',
                                label: T._('Use roughness from main texture', 'Admin'),
                                checked: material.clearCoat.useRoughnessFromMainTexture,
                                onChange: (checked) => {
                                    material.clearCoat.useRoughnessFromMainTexture = checked;
                                    helpers.updateState(material);
                                }
                            }),
                            !material.clearCoat.useRoughnessFromMainTexture && [...createTexturePropertyConfig(material, helpers, 'clearCoat_textureRoughness')]
                        ]
                            .flat()
                            .filter(Boolean) as PropertiesPanelPropertyConfig[]
                    })
                ]
            ]
                .flat()
                .filter(Boolean) as PropertiesPanelPropertyConfig[]
        });
    };

    return [
        createIntensityGroup(),
        createCameraGroup(),
        createAlbedoGroup(),
        createAmbientGroup(),
        createOpacityGroup(),
        createReflectionGroup(),
        createEmissiveGroup(),
        createReflectivityGroup(),
        createMetallicGroup(),
        createMicroSurfaceGroup(),
        createNormalGroup(),
        createRefractionGroup(),
        createLightmapGroup(),
        createFlagsGroup(),
        createClearCoatGroup(),
        createAnisotropicConfig(),
        createSheenConfig(),
        createSubSurfaceConfig()
    ];
};

const materialPropertiesByType = (material: BABYLON.Material, helpers: MaterialConfigHelpers): PropertiesPanelPropertyConfig[] => {
    if (material instanceof BABYLON.StandardMaterial) {
        return standardMaterialProperties(material, helpers);
    }
    if (material instanceof BABYLON.PBRMetallicRoughnessMaterial) {
        return pbrMetallicRoughnessMaterialProperties(material, helpers);
    }
    if (material instanceof BABYLON.PBRSpecularGlossinessMaterial) {
        return pbrSpecularGlossinessMaterialProperties(material, helpers);
    }
    if (material instanceof BABYLON.PBRMaterial) {
        return pbrMaterialProperties(material, helpers);
    }
    return [];
};

const transparencyOptions = [
    { value: -1, label: T._('Auto', 'Composer') },
    { value: 0, label: T._('Opaque', 'Composer') },
    { value: 1, label: T._('Alpha Test', 'Composer') },
    { value: 2, label: T._('Alpha Blend', 'Composer') },
    { value: 3, label: T._('Alpha Blend + Test', 'Composer') }
];

export const useMaterialsPropertiesCreator = (): ((material: BABYLON.Material, helpers: MaterialConfigHelpers) => PropertiesPanelPropertyConfig[]) => {
    const { showDialog, closeDialog } = useDialogManager();
    const preview3DStore = useContext(Preview3DStoreContext);

    return (material: BABYLON.Material, helpers: MaterialConfigHelpers) =>
        [
            createPropertyConfig({
                type: 'icon',
                id: 'dam-replace',
                label: T._('Replace from DAM', 'Admin'),
                icon: <ReplaceSolid />,
                onIconClick: () => {
                    showDialog(
                        'dam-dialog',
                        <Preview3DStoreContext.Provider value={preview3DStore}>
                            <DAMDialog
                                isMaterialReplacement
                                onClose={(productAssetUrl: string) => {
                                    closeDialog('dam-dialog');
                                    showDialog(
                                        'upload-file',
                                        <Preview3DStoreContext.Provider value={preview3DStore}>
                                            <UploadDialog
                                                damProductAssetUrl={productAssetUrl}
                                                onComplete={(meshes) => {
                                                    closeDialog('upload-file');

                                                    let damMaterial = meshes.find((mesh) => mesh.material !== null)!.material;
                                                    damMaterial!.id = material.id;
                                                    damMaterial!.name = material.name;

                                                    helpers.sceneEditor!.getMeshesUsingMaterial(material.id).forEach((mesh) => {
                                                        mesh.material = damMaterial;
                                                    });

                                                    material.dispose();
                                                    meshes.forEach((mesh) => {
                                                        mesh.dispose();
                                                    });

                                                    helpers.updateState(damMaterial!, true);
                                                }}
                                            />
                                        </Preview3DStoreContext.Provider>
                                    );
                                }}
                            />
                        </Preview3DStoreContext.Provider>
                    );
                }
            }),
            createPropertyConfig({
                type: 'text',
                id: 'name',
                label: T._('Name', 'Admin'),
                value: material.name,
                onChange: (newValue) => {
                    if (helpers.handleMaterialNameChange) {
                        helpers.handleMaterialNameChange(newValue);
                    } else {
                        // NON chiamare helpers.sceneEditor.renameMaterial per evitare loop di eventi
                        // Aggiorna il nome direttamente sul materiale BABYLON
                        material.name = newValue || 'Material';
                        helpers.updateState(material);
                    }
                }
            }),
            createPropertyConfig({
                type: 'range',
                id: 'material-opacity',
                label: T._('Opacity', 'Admin'),
                value: material.alpha * 100,
                min: 0,
                max: 100,
                onChange: (newValue) => {
                    material.alpha = newValue / 100;
                    helpers.updateState(material);
                }
            }),
            createPropertyConfig({
                type: 'select',
                id: 'material-transparency-mode',
                label: T._('Transparency Mode', 'Admin'),
                options: transparencyOptions,
                value: transparencyOptions.find((x) => x.value === (material.transparencyMode ?? -1)),
                onChange: (newValue) => {
                    const value = (newValue?.value as number) ?? -1;
                    material.transparencyMode = value === -1 ? null : value;
                    helpers.updateState(material);
                }
            }),

            createPropertyConfig({
                type: 'checkbox',
                id: 'backFaceCulling',
                label: T._('Double sided', 'Admin'),
                checked: !material.backFaceCulling,
                onChange: (newValue) => {
                    material.backFaceCulling = !newValue;
                    helpers.updateState(material);
                }
            }),
            createPropertyConfig({
                type: 'checkbox',
                id: 'needDepthPrePass',
                label: T._('Need Depth Pre-Pass', 'Admin'),
                checked: material.needDepthPrePass,
                onChange: (newValue) => {
                    material.needDepthPrePass = newValue;
                    helpers.updateState(material);
                }
            }),
            createPropertyConfig({
                type: 'range',
                id: 'zOffset',
                label: T._('Z Offset', 'Admin'),
                value: material.zOffset,
                min: 0,
                max: 100,
                step: 1,
                onChange: (newValue) => {
                    material.zOffset = newValue;
                    helpers.updateState(material);
                }
            }),
            // TODO check if needed to be reimplemented
            // {this.props.zkScene.getInfoPointsEnabled() && (
            // 		<PropertiesPanelGroup label={T._('Material photos', 'Composer')}>
            // 			<PropertiesPanelMaterialPreviewProperty
            // 				dataTrack={'material photos'}
            // 				photos={this.props.zkScene.get('materialPhotos').getMaterialPhotoUrls(material.id)}
            // 				onChange={this.handleMaterialPhotoUpload}
            // 				onRemove={this.handleMaterialPhotoRemove}
            // 				loading={this.state.isMaterialPhotoLoading}
            // 			/>
            // 		</PropertiesPanelGroup>
            // 	)}
            ...materialPropertiesByType(material, helpers)
        ]
            .flat()
            .filter(Boolean) as PropertiesPanelPropertyConfig[];
};
